import type { QueryClient } from "@tanstack/react-query";

import type { Deployment, OdbRefreshedDossier, OrderFieldSpecs, OrdersRefreshed, SubordinateFollowed } from "../api/types";
import { attentionSelector, designateSoon } from "../components/attention";
import { unitDesignation } from "../i18n/designation";
import { t, tServer } from "../i18n/i18n";
import { plural } from "../plural";
import { type Notice, useUi } from "../state/ui";
import { ORDER_REOPEN } from "../views/order/attentionTargets";
import { changedSections } from "../views/order/changes";

/** L'ordre suit la carte : après une modification (unités ou lignes déplacées, ponts détruits, composition, Données
 *  initiales…), le serveur recompose les OPO en cours de rédaction, et ceux qui sont validés si leur texte change
 *  (remis en rédaction, à valider de nouveau ; jamais en CENTAC, où un OPO validé est déjà joué). La carte les fait
 *  recharger et le dit dans son avis : sections mises à jour, OPO inchangé, OPO remis en rédaction, échec ; ses
 *  actions mènent aux changements surlignés dans l'OPO (demande d'Alexis du 13/10/2026). */

/** Recomposition des OPO portée par une réponse : déploiement après une modification, ou dossier enregistré
 *  (`Exercise.refreshed`). `centac` : rotation CENTAC (déploiement), dont les OPO validés sont déjà joués. */
export interface OrdersRefresh {
  orders?: OrdersRefreshed | null;
  /** Paragraphes modifiés à la main devenus obsolètes, par numéro d'OPO (docs/18 §5.4). */
  outdated?: Record<string, string[]> | null;
  centac?: unknown;
}

/** Nombre de paragraphes modifiés à la main à revoir, tous OPO confondus. */
export function outdatedCount(result: OrdersRefresh): number {
  return Object.values(result.outdated ?? {}).reduce((n, fields) => n + fields.length, 0);
}

// --- Sections de l'ordre --------------------------------------------------------------------------------------

/** Champs de l'éditeur de chaque OPO déjà lus sur ce poste (vue de l'ordre) : l'avis de la carte nomme les sections
 *  mises à jour d'après leurs onglets ; sans eux, il compte les paragraphes. */
const knownSpecs = new Map<string, OrderFieldSpecs>();

export function rememberOrderSpecs(exerciseId: string, number: number, specs: OrderFieldSpecs): void {
  knownSpecs.set(`${exerciseId}:${number}`, specs);
}

/** Champs de l'éditeur d'un OPO du dossier ouvert, s'ils ont déjà été lus. */
export function rememberedSpecs(number: number, exerciseId = currentDossier()): OrderFieldSpecs | null {
  return exerciseId ? (knownSpecs.get(`${exerciseId}:${number}`) ?? null) : null;
}

function currentDossier(): string | null {
  const ui = useUi.getState();
  return ui.deploymentParams?.exercise_id ?? ui.exerciseId;
}

/** Au plus trois sections nommées, puis « et {n} autre(s) ». */
export function sectionList(sections: readonly string[]): string {
  if (sections.length <= 3) return sections.join(", ");
  const rest = sections.length - 3;
  const list = sections.slice(0, 3).join(", ");
  return rest === 1 ? t("{list} et 1 autre", { list }) : t("{list} et {n} autres", { list, n: rest });
}

/** Ce qui a changé dans les OPO `numbers` : sections nommées (onglets connus), sinon nombre de paragraphes. */
function changedPart(result: OrdersRefresh, numbers: readonly number[], specsOf: (n: number) => OrderFieldSpecs | null): string {
  const sections: string[] = [];
  let unknown = 0;
  let total = 0;
  for (const n of numbers) {
    const keys = result.orders?.changed?.[String(n)] ?? [];
    total += keys.length;
    const specs = specsOf(n);
    if (!specs) {
      unknown += keys.length;
      continue;
    }
    const found = changedSections(keys, specs);
    for (const section of found.sections) if (!sections.includes(section)) sections.push(section);
    unknown += found.unknown;
  }
  if (sections.length) return sectionList(sections);
  return total ? plural(unknown || total, t("paragraphe"), t("paragraphes")) : "";
}

export interface OrdersNoteOptions {
  /** Niveau du dossier changé (Données initiales) : un OPO validé resté tel quel garde l'ancien niveau. */
  levelChanged?: boolean;
  /** Origine de la modification : la carte (défaut) ou les Données initiales et l'ordre de bataille (« le dossier »). */
  source?: "carte" | "dossier";
  /** Rotation CENTAC : un OPO validé est déjà joué, jamais remis en rédaction (défaut : lu sur la réponse). */
  centac?: boolean;
  /** Champs de l'éditeur d'un OPO (sections nommées) ; défaut : ceux déjà lus sur ce poste. */
  specsOf?: (number: number) => OrderFieldSpecs | null;
}

const list = (numbers: readonly number[]) => numbers.map((n) => t("N°{n}", { n })).join(", ");

/** Mention des ordres dans un avis : OPO mis à jour (sections nommées), inchangés, validés remis en rédaction ou
 *  laissés tels quels, en échec, paragraphes modifiés à la main à revoir. `levelChanged` (ou les options) : voir
 *  `OrdersNoteOptions`. */
export function ordersNote(result: OrdersRefresh, options: boolean | OrdersNoteOptions = {}): string {
  const opts: OrdersNoteOptions = typeof options === "boolean" ? { levelChanged: options } : options;
  // Rotation CENTAC : dite par le déploiement (`centac`) ou par le relevé du serveur (`orders.centac`, écritures du
  // dossier : Données initiales, ordre de bataille, ordres reçus).
  const centac = opts.centac ?? (Boolean(result.centac) || Boolean(result.orders?.centac));
  const specsOf = opts.specsOf ?? ((n: number) => rememberedSpecs(n));
  const orders = result.orders;
  const reopened = orders?.reopened ?? [];
  const failed = orders?.failed ?? [];
  const unchangedAll = orders?.unchanged ?? [];
  const finalized = (orders?.finalized ?? []).filter((n) => !reopened.includes(n));
  const updated = (orders?.updated ?? []).filter((n) => !reopened.includes(n));
  const unchanged = unchangedAll.filter((n) => !finalized.includes(n) && !updated.includes(n));
  const parts: string[] = [];
  // OPLAN du corps d'armée (document n° 0, docs/08 §25) : mis à jour avant l'OPO qui en est tiré.
  const oplan = orders?.oplan;
  if (oplan?.reopened) {
    parts.push(t("OPLAN validé : remis en rédaction et mis à jour d'après le dossier. Validez-le de nouveau."));
  } else if (oplan?.updated) {
    const specs = specsOf(0);
    const found = specs && oplan.changed?.length ? changedSections(oplan.changed, specs).sections : [];
    parts.push(
      found.length
        ? t("OPLAN mis à jour : {sections}.", { sections: sectionList(found) })
        : t("OPLAN mis à jour."),
    );
  }
  if (updated.length) {
    const what = changedPart(result, updated, specsOf);
    parts.push(
      what
        ? t("OPO {list} mis à jour : {sections}.", { list: list(updated), sections: what })
        : t("OPO {list} mis à jour.", { list: list(updated) }),
    );
  }
  if (reopened.length) {
    const fromMap = (opts.source ?? "carte") === "carte";
    parts.push(
      reopened.length > 1
        ? fromMap
          ? t("OPO {list} validés : remis en rédaction et mis à jour d'après la carte. Validez-les de nouveau.", { list: list(reopened) })
          : t("OPO {list} validés : remis en rédaction et mis à jour d'après le dossier. Validez-les de nouveau.", { list: list(reopened) })
        : fromMap
          ? t("OPO {list} validé : remis en rédaction et mis à jour d'après la carte. Validez-le de nouveau.", { list: list(reopened) })
          : t("OPO {list} validé : remis en rédaction et mis à jour d'après le dossier. Validez-le de nouveau.", { list: list(reopened) }),
    );
  }
  if (unchanged.length) {
    parts.push(
      unchanged.length > 1
        ? t("OPO {list} inchangés : cette modification n'apparaît pas dans les ordres.", { list: list(unchanged) })
        : t("OPO {list} inchangé : cette modification n'apparaît pas dans l'ordre.", { list: list(unchanged) }),
    );
  }
  if (finalized.length) {
    // CENTAC : OPO validés déjà joués, jamais rouverts d'office. Ailleurs : validé dont le texte n'a pas changé
    // (dans `unchanged`), ou réponse d'un serveur antérieur (à rouvrir pour le mettre à jour).
    const played = centac ? finalized : [];
    const kept = centac ? [] : finalized.filter((n) => unchangedAll.includes(n));
    const legacy = centac ? [] : finalized.filter((n) => !unchangedAll.includes(n));
    if (played.length) {
      parts.push(
        played.length > 1
          ? t("OPO {list} déjà joués : inchangés.", { list: list(played) })
          : t("OPO {list} déjà joué : inchangé.", { list: list(played) }),
      );
    }
    if (kept.length) {
      parts.push(
        kept.length > 1
          ? t("OPO {list} validés : inchangés.", { list: list(kept) })
          : t("OPO {list} validé : inchangé.", { list: list(kept) }),
      );
    }
    if (legacy.length && opts.levelChanged) {
      parts.push(
        legacy.length > 1
          ? t("OPO {list} validés au niveau précédent : rouvrez-les pour les recomposer au nouveau niveau.", { list: list(legacy) })
          : t("OPO {list} validé au niveau précédent : rouvrez-le pour le recomposer au nouveau niveau.", { list: list(legacy) }),
      );
    } else if (legacy.length) {
      parts.push(
        legacy.length > 1
          ? t("OPO {list} validés : à rouvrir pour les mettre à jour.", { list: list(legacy) })
          : t("OPO {list} validé : à rouvrir pour le mettre à jour.", { list: list(legacy) }),
      );
    }
  }
  if (failed.length) {
    parts.push(
      failed.length > 1
        ? t("OPO {list} non mis à jour : ils le seront à leur prochaine ouverture.", { list: list(failed) })
        : t("OPO {list} non mis à jour : il le sera à sa prochaine ouverture.", { list: list(failed) }),
    );
  }
  const outdated = outdatedCount(result);
  if (outdated)
    parts.push(
      t("{paragraphs} à la main à revoir.", {
        paragraphs: plural(outdated, t("paragraphe modifié"), t("paragraphes modifiés")),
      }),
    );
  const subordinates = subordinatesNote(orders?.subordinates);
  if (subordinates) parts.push(subordinates);
  return parts.join(" ");
}

/** Cascade des ordres (décisions d'Alexis du 13/10/2026) : dossiers subordonnés dont l'OPO a suivi l'ordre écrit
 *  (« Ordres subordonnés mis à jour : 1erRTEST, 2eRTEST. »), OPO validés remis en rédaction, ceux qui suivront à leur
 *  ouverture, les annotations reprises (« Annotations reprises par les subordonnés : 1er GTIA. ») et les dossiers
 *  transmis prévenus (« Ordres transmis prévenus : 1er GTIA. »). Vide sans subordonné touché. */
export function subordinatesNote(subordinates: readonly SubordinateFollowed[] | null | undefined): string {
  const name = (s: SubordinateFollowed) => (s.label ? unitDesignation(s.label) : s.name);
  // Dossiers transmis à un autre niveau que la compagnie : prévenus, ils ne suivent pas (13/10/2026).
  const notified = (subordinates ?? []).filter((s) => s.notified);
  // Unités retirées de l'ordre (audit du 15/10/2026) : leur dossier le signale, il ne suit plus.
  const removed = (subordinates ?? []).filter((s) => s.removed && !s.notified);
  // Annotations du chef reprises (lecture seule) sans que l'ordre du subordonné change (15/10/2026).
  const annotated = (subordinates ?? []).filter(
    (s) => s.annotations && !s.notified && !s.removed && !s.deferred && !s.updated?.length && !s.reopened?.length,
  );
  const cascade = (subordinates ?? []).filter((s) => !s.notified && !s.removed && !annotated.includes(s));
  const followed = cascade.filter((s) => !s.deferred);
  const reopened = followed.filter((s) => s.reopened?.length);
  const later = cascade.filter((s) => s.deferred);
  const parts: string[] = [];
  if (followed.length) parts.push(t("Ordres subordonnés mis à jour : {names}.", { names: sectionList(followed.map(name)) }));
  if (reopened.length) {
    parts.push(
      reopened.length > 1
        ? t("Leurs OPO validés sont remis en rédaction : {names}.", { names: sectionList(reopened.map(name)) })
        : t("OPO validé remis en rédaction : {name}.", { name: name(reopened[0]!) }),
    );
  }
  if (later.length) {
    parts.push(
      later.length === 1
        ? t("L'ordre de {name} sera mis à jour à son ouverture.", { name: name(later[0]!) })
        : followed.length
          ? t("Les ordres de {n} autres subordonnés seront mis à jour à leur ouverture.", { n: later.length })
          : t("Les ordres de {n} subordonnés seront mis à jour à leur ouverture.", { n: later.length }),
    );
  }
  if (annotated.length)
    parts.push(t("Annotations reprises par les subordonnés : {names}.", { names: sectionList(annotated.map(name)) }));
  if (notified.length) parts.push(t("Ordres transmis prévenus : {names}.", { names: sectionList(notified.map(name)) }));
  if (removed.length)
    parts.push(t("Retirés de l'ordre : {names} ; leur dossier le signale.", { names: sectionList(removed.map(name)) }));
  return parts.join(" ");
}

// --- Actions des avis -------------------------------------------------------------------------------------------

type NoticeAction = NonNullable<Notice["action"]>;

/** OPO dont des paragraphes ont été marqués par l'écriture : l'OPO affiché d'abord (CENTAC), puis les remis en
 *  rédaction, puis par numéro. */
export function changedOrders(result: OrdersRefresh): number[] {
  const changed = Object.entries(result.orders?.changed ?? {})
    .filter(([, keys]) => keys.length)
    .map(([n]) => Number(n))
    .filter((n) => Number.isInteger(n));
  const reopened = new Set(result.orders?.reopened ?? []);
  const shown = useUi.getState().opo;
  const rank = (n: number) => (n === shown ? 0 : reopened.has(n) ? 1 : 2);
  return changed.sort((a, b) => rank(a) - rank(b) || a - b);
}

/** Action « Voir les changements » quand l'écriture a marqué des paragraphes : ouvre l'OPO sur le premier. */
export function changesAction(result: OrdersRefresh, open: (number: number) => void = openChangedOrder): NoticeAction | undefined {
  const first = changedOrders(result)[0];
  return first === undefined ? undefined : { label: t("Voir les changements"), run: () => open(first) };
}

/** Action « Voir l'ordre » d'un avis quand des paragraphes modifiés à la main sont à revoir (docs/18 §6.7). */
export function outdatedAction(result: OrdersRefresh, open: () => void): NoticeAction | undefined {
  return outdatedCount(result) ? { label: t("Voir l'ordre"), run: open } : undefined;
}

/** Action « Ouvrir l'OPO » d'un avis quand un OPO validé est resté tel qu'il a été signé alors que l'écriture le
 *  concernait (réponse d'un serveur antérieur, ou niveau changé) : `open` ouvre le premier et désigne « Rouvrir ».
 *  Jamais pour un OPO validé resté inchangé, ni pour un OPO déjà joué de la rotation CENTAC. */
export function finalizedAction(result: OrdersRefresh, open: (number: number) => void): NoticeAction | undefined {
  if (result.centac || result.orders?.centac) return undefined;
  const reopened = result.orders?.reopened ?? [];
  const unchanged = result.orders?.unchanged ?? [];
  const first = result.orders?.finalized?.find((n) => !reopened.includes(n) && !unchanged.includes(n));
  return first === undefined ? undefined : { label: t("Ouvrir l'OPO"), run: () => open(first) };
}

/** Actions d'un avis d'écriture : « Annuler » d'abord (si l'écriture s'est inscrite dans l'historique), puis, par
 *  ordre de priorité, « Voir les changements », « Voir l'ordre » (paragraphes à revoir) ou « Ouvrir l'OPO » (OPO
 *  validé à rouvrir). Sans « Annuler », l'action de l'ordre passe en premier. */
export function orderActions(undo: Notice["action"], result: OrdersRefresh): Pick<Notice, "action" | "secondary"> {
  const order =
    changesAction(result) ?? outdatedAction(result, openOutdatedOrder) ?? finalizedAction(result, openFinalizedOrder);
  return undo ? { action: undo, secondary: order } : { action: order };
}

/** Demande de la vue de l'ordre : se placer sur le premier paragraphe mis à jour à son ouverture. Elle vaut le temps
 *  que l'ordre recomposé arrive (10 s), pas au-delà : une ouverture ultérieure de l'OPO ne saute pas d'elle-même. */
let changesFocus: { number: number; at: number } | null = null;
const CHANGES_FOCUS_MS = 10_000;

/** Prend (et efface) la demande de placement sur les changements, si elle vise cet OPO et n'a pas expiré. */
export function takeChangesFocus(number: number, now = Date.now()): boolean {
  const request = changesFocus;
  if (!request || request.number !== number) return false;
  changesFocus = null;
  return now - request.at <= CHANGES_FOCUS_MS;
}

/** Ouvre l'OPO `number` sur son premier paragraphe mis à jour (« Voir les changements ») ; en CENTAC, l'OPO
 *  affiché devient celui-là. */
export function openChangedOrder(number: number): void {
  const ui = useUi.getState();
  if (ui.deploymentParams?.level === "CENTAC" && ui.opo !== number) ui.setOpo(number);
  changesFocus = { number: ui.deploymentParams?.level === "CENTAC" ? number : 1, at: Date.now() };
  ui.setView("order");
}

/** Ouvre l'ordre et désigne ses paragraphes à revoir. */
export function openOutdatedOrder(): void {
  useUi.getState().setView("order");
  if (typeof document !== "undefined") designateSoon('[data-attention="order-outdated"]', "auto", 2000);
}

/** Ouvre un OPO finalisé resté tel qu'il a été signé (OPO de la rotation CENTAC compris) et désigne « Rouvrir ». */
export function openFinalizedOrder(number: number): void {
  const ui = useUi.getState();
  if (ui.opo !== number) ui.setOpo(number);
  ui.setView("order");
  if (typeof document !== "undefined") designateSoon(attentionSelector(ORDER_REOPEN), "auto", 2000);
}

/** Avis du serveur portés par la réponse (`notices` : fuseau automatique rétabli, état effacé…), traduits. */
export function serverNotes(result: Deployment): string {
  return (result.notices ?? [])
    .map((n) => tServer(n).trim())
    .filter(Boolean)
    .map((n) => (/[.!?…]$/.test(n) ? n : `${n}.`)) // phrases du serveur sans point final, suivies d'autres
    .join(" ");
}

/** Détail d'un avis complété des avis du serveur et de la mention des ordres. */
export function withOrders(detail: string | undefined, result: Deployment): string | undefined {
  return [serverNotes(result), detail, ordersNote(result, { centac: Boolean(result.centac) })].filter(Boolean).join(" ") || undefined;
}

/** Détail d'un avis du dossier (Données initiales, report d'un ordre reçu, ordre de bataille) complété de la mention
 *  des ordres recomposés (`Exercise.refreshed`, d'origine « le dossier »). */
export function withRefreshed(detail: string | undefined, refreshed: OrdersRefresh | null | undefined): string | undefined {
  const note = refreshed ? ordersNote(refreshed, { source: "dossier" }) : "";
  return [detail, note].filter(Boolean).join(" ") || undefined;
}

/** Mention et action d'un avis d'ordre de bataille enregistré (ou supprimé de force) : OPO recomposés dans les
 *  dossiers qui l'emploient. Le dossier ouvert est détaillé comme un avis du dossier (sections mises à jour, OPO
 *  validé remis en rédaction, paragraphes à revoir) et son action mène aux changements ; les autres sont comptés,
 *  ceux dont les OPO ne sont recomposés qu'à leur ouverture (`deferred`) à part. */
export function odbRefreshed(
  refreshed: readonly OdbRefreshedDossier[] | null | undefined,
  current: string | null = currentDossier(),
): { note: string; action?: NoticeAction } {
  const mine = (refreshed ?? []).find((d) => d.id === current);
  const touched = (refreshed ?? []).filter((d) => d.orders?.updated.length && d !== mine);
  const own = mine?.orders && (mine.orders.updated.length || outdatedCount(mine)) ? ordersNote(mine, { source: "dossier" }) : "";
  const others = touched.length
    ? touched.length > 1
      ? own
        ? t("OPO mis à jour dans {n} autres dossiers.", { n: touched.length })
        : t("OPO mis à jour dans {n} dossiers.", { n: touched.length })
      : t("OPO mis à jour dans le dossier « {name} ».", { name: touched[0]!.name })
    : "";
  const deferred = (refreshed ?? []).filter((d) => d.deferred && d !== mine);
  const later = deferred.length
    ? deferred.length > 1
      ? t("Les OPO de {n} dossiers seront mis à jour à leur ouverture.", { n: deferred.length })
      : t("Les OPO du dossier « {name} » seront mis à jour à leur ouverture.", { name: deferred[0]!.name })
    : "";
  const note = [own, others, later].filter(Boolean).join(" ");
  return { note, action: mine?.orders ? (changesAction(mine) ?? outdatedAction(mine, openOutdatedOrder)) : undefined };
}

/** Requêtes relues quand l'ordre du dossier a pu changer : texte, mise en page, champs de l'éditeur (le niveau de
 *  l'ordre, donc ses paragraphes, a pu changer : annexes de la division), versions, rotation ; dossier d'exercice
 *  (`briefingKey`) et fiche pays (unités ENI de l'ordre, `countrySheetKey`), état de la déclinaison (« à mettre à
 *  jour ») et document regroupé, qui en dépendent (audit du 10/10/2026 : sans quoi ils restaient périmés jusqu'à une
 *  minute). */
export const ORDER_DEPENDENT_KEYS = [
  "order",
  "order-layout",
  "order-fields",
  "order-versions",
  "rotation",
  "briefing",
  "country-sheet",
  "declination",
  "grouped",
  // Cohérence tactique (docs/24) : les constats des documents suivent l'ordre.
  "coherence",
] as const;

/** Ordres du dossier, et ce qui en dépend, rechargés à leur prochain affichage. */
export function refreshOrders(queryClient: QueryClient, exerciseId: string | null | undefined): void {
  if (!exerciseId) return;
  for (const key of ORDER_DEPENDENT_KEYS) {
    void queryClient.invalidateQueries({ queryKey: [key, exerciseId] });
  }
}
